import * as React from 'react'
import { all, call, put, select, takeEvery } from 'redux-saga/effects'
import api from '../../../services/api.services'
import {
  ActivityProgress,
  CustomType,
  ExportGoalResults$Response,
  GetGoalCountryProgress$Response,
  GetGoalGroupProgress$Response,
  GetGoalIndividualProgress$Response,
  GetGoalsWithProgress$Response,
  GetLeaderboardCountries$Response,
  GetLeaderboardGroups$Response,
  GetLeaderboardPeople$Response,
  GetUserGoalProgress$Response,
  GoalId,
  Lang,
  LeaveGoal$Request,
  ProgressType,
  Role,
  UserId,
  UserProfileDto,
} from '../../../services/api.types'
import {
  getCurrentCompanyGoal,
  getCurrentCompanyGoalSuccess,
  getCurrentGoalActivityList,
  getCurrentGoalActivityListSuccess,
  getCurrentGoalExport,
  getCurrentLeaderboard,
  joinGoal,
  leaveGoal,
} from './ActivityActions'
import { Action } from 'redux-actions'
import { getUserDto, getUserIntegrationScopes, hasRole } from '../../../common/auth/authSelectors'
import { ActivityView } from './ActivityReducer'
import { getActivityFilter, getActivityView, getGroupSum, getGroupView } from './ActivitySelectors'
import { download } from '../../../common/helpers/downloadHelpers'
import { INotification } from '../../../common/notifications/NotificationReducers'
import { t } from 'ttag'
import { addNotification, clearNotification } from '../../../common/notifications/NotificationActions'
import { getCompanyDivisionsSuccess, getUserCompanyCountriesSuccess } from '../../Settings/store/SettingsActions'
import { scrollToTop } from '../../../common/helpers/scroll'
import YumSetupActivityDataWrapper from '../../../components/YumSetupActivityData/YumSetupActivityDataWrapper'
import { getErrorMsg } from '../../../common/helpers/translations'
import {
  isActivityIndexType,
  isCollectiveType,
  isManualEntryOnlyType,
  isStepsType,
} from '../../../common/helpers/challenges'
import YumChooseIntegrationBtnWrapper from '../../../components/YumChooseIntegrationBtn/YumChooseIntegrationBtnWrapper'
import { currentLang } from '../../../i18nInit'
import buildVersion from '../../../version.json'
import { setLoadingJoinGoal, setLoadingJoinGoalDone } from '../../../common/loading/loadingActions'
import { getCustomTypeWithLegacy } from '../../../services/CustomTypes'
import { refreshChallengeResults } from '../../Progress/store/ProgressActions'
import moment from 'moment/moment'
import { convertUTCToLocalDate } from '../../../common/helpers/date'

const { version } = buildVersion || { version: '' }

const getUserDetails = (userId: UserId, activityProgress: { [p: string]: ActivityProgress }) => {
  const userData = Object.entries(activityProgress || {}).find(([compareID]) => compareID === userId)
  const [, userDetails] = userData || ['', null]
  return userDetails as ActivityProgress
}

function* sagaGetCurrentGoalActivityList({ payload }: Action<any>) {
  // Leaderboard (with token auth).
  if (payload.token) {
    yield put({ type: String(getCurrentLeaderboard), payload })
    return
  }

  const { goalId, divisionId } = payload || {}
  let { activityFilter, activityView, groupView, groupSum } = payload || {}
  if (!activityFilter) {
    // @ts-ignore
    activityFilter = yield select(getActivityFilter, goalId)
  }
  if (!activityView) {
    // @ts-ignore
    activityView = yield select(getActivityView, goalId)
  }
  if (!groupView) {
    // @ts-ignore
    groupView = yield select(getGroupView, goalId)
  }
  if (groupSum === undefined) {
    // @ts-ignore
    groupSum = yield select(getGroupSum, goalId)
  }

  try {
    const outputPayload = {
      goalId,
      activityFilter,
      userProgress: undefined,
      activityProgress: undefined,
      divisionActivityProgress: undefined,
      countryActivityProgress: undefined,
      userIsInActivityList: false,
    } as any

    const userDto: UserProfileDto = yield select(getUserDto)
    const userId = userDto.userId

    if (activityView === ActivityView.INDIVIDUAL) {
      const payload: GetGoalIndividualProgress$Response = yield call(api.activity.getGoalIndividualProgress, {
        ...activityFilter,
        goalId,
        skipJoin: true,
      })
      const activityProgress = payload.activityProgress
      outputPayload.userIsInActivityList = Object.keys(activityProgress || {}).some((compareID) => compareID === userId)
      outputPayload.activityProgress = activityProgress ? activityProgress : []
      outputPayload.activityProgress = activityProgress
    } else if (activityView === ActivityView.GROUP) {
      const payload: GetGoalGroupProgress$Response = yield call(api.activity.getGoalGroupProgress, {
        groupView,
        goalId,
        sum: groupSum,
      })
      outputPayload.groupView = groupView
      const activityProgress = payload.divisionActivityProgress
      /*if (groupView === GroupView.COUNTRIES_MERGED) {
        outputPayload.userIsInActivityList = Object.values(activityProgress || {}).some(
          (row) => row.divisionId === divisionId, //userDto.divisionId,
        )
      } else {
        outputPayload.userIsInActivityList = Object.values(activityProgress || {}).some(
          (row) => row.divisionId === divisionId && row.country === userDto.country,
        )
      }*/
      outputPayload.divisionActivityProgress = activityProgress
    } else {
      const payload: GetGoalCountryProgress$Response = yield call(api.activity.getGoalCountryProgress, {
        goalId,
        sum: groupSum,
      })
      const activityProgress = payload.countryActivityProgress
      /*outputPayload.userIsInActivityList = Object.values(activityProgress || {}).some(
        (row) => row.country === userDto.country,
      )*/
      outputPayload.countryActivityProgress = activityProgress
    }

    //const isAdmin = yield select(hasRole, Role.ADMIN)
    //if (!isAdmin) {
    // TODO: We should check this based on current goal/companyId.
    const isUser: boolean = yield select(hasRole, Role.USER)
    if (isUser) {
      // If user is in activity list, we can get the progress from there, otherwise we need a separate call.
      if (activityView === ActivityView.INDIVIDUAL && outputPayload.userIsInActivityList) {
        outputPayload.userProgress = getUserDetails(userId, outputPayload.activityProgress)
      } else {
        const { activityProgress: userActivityProgress }: GetUserGoalProgress$Response = yield call(
          api.activity.getUserGoalProgress,
          { goalId },
        )
        outputPayload.userProgress = getUserDetails(userId, userActivityProgress)
      }
    } else {
      outputPayload.userProgress = null
    }

    yield put({ type: String(getCurrentGoalActivityListSuccess), payload: outputPayload })
  } catch (e: any) {
    console.error('GetActivityListFailed: ', e)
  }
}

function* sagaGetCurrentLeaderboard({ payload }: Action<any>) {
  const { goalId, token } = payload || {}
  let { activityFilter, activityView, groupView, groupSum } = payload || {}
  if (!activityFilter) {
    // @ts-ignore
    activityFilter = yield select(getActivityFilter, goalId)
  }
  if (!activityView) {
    // @ts-ignore
    activityView = yield select(getActivityView, goalId)
  }
  if (!groupView) {
    // @ts-ignore
    groupView = yield select(getGroupView, goalId)
  }
  if (groupSum === undefined) {
    // @ts-ignore
    groupSum = yield select(getGroupSum, goalId)
  }

  try {
    const outputPayload = {
      goalId,
      activityFilter,
      userProgress: null,
      activityProgress: undefined,
      divisionActivityProgress: undefined,
      countryActivityProgress: undefined,
      userIsInActivityList: false,
    } as any

    let data
    if (activityView === ActivityView.INDIVIDUAL) {
      const response: GetLeaderboardPeople$Response = yield call(api.activity.getLeaderboardPeople, {
        ...activityFilter,
        goalId,
        skipJoin: true,
        token,
      })
      outputPayload.activityProgress = response.activityProgress
      data = response
    } else if (activityView === ActivityView.GROUP) {
      const response: GetLeaderboardGroups$Response = yield call(api.activity.getLeaderboardGroups, {
        groupView,
        goalId,
        sum: groupSum,
        token,
      })
      outputPayload.groupView = groupView
      outputPayload.divisionActivityProgress = response.divisionActivityProgress
      data = response
    } else {
      const response: GetLeaderboardCountries$Response = yield call(api.activity.getLeaderboardCountries, {
        goalId,
        sum: groupSum,
        token,
      })
      outputPayload.countryActivityProgress = response.countryActivityProgress
      data = response
    }

    yield put({ type: String(getCompanyDivisionsSuccess), payload: data.divisionDtos })
    yield put({ type: String(getUserCompanyCountriesSuccess), payload: data.companyCountries })
    yield put({ type: String(getCurrentCompanyGoalSuccess), payload: data.goals })

    yield put({ type: String(getCurrentGoalActivityListSuccess), payload: outputPayload })
  } catch (e: any) {
    console.error('GetActivityListFailed: ', e)

    if (e.status === 401) {
      const notification: INotification = {
        type: 'warning',
        content: t`Unauthorized!`,
      }
      yield put({ type: String(addNotification), payload: notification })
    }
  }
}

function* sagaGetCurrentCompanyGoal({ payload }: Action<any>) {
  try {
    // Leaderboard (with token auth).
    if (payload && payload.token) {
      yield put({ type: String(getCurrentLeaderboard), payload: { goalId: payload.id, token: payload.token } })
      return
    }

    const { goals }: GetGoalsWithProgress$Response = yield call(api.activity.getChallengesWithProgress, { version })
    // const { goals }: GetGoalsWithProgress$Response = yield call(api.activity.getGoalsWithProgress)
    yield put({ type: String(getCurrentCompanyGoalSuccess), payload: goals ? goals : [] })

    if (payload && payload.updateList && payload.id) {
      yield put({
        type: String(getCurrentGoalActivityList),
        payload: { goalId: payload.id, companyId: payload.companyId },
      })
    }
  } catch (e: any) {
    console.error('GetCompanyGoalsFailed: ', e)
  }
}

function* sagaGetCurrentGoalExport({ payload }: Action<any>) {
  try {
    yield put({ type: String(clearNotification) })
    const { fileContent, fileMediaType, fileName }: ExportGoalResults$Response = yield call(
      api.activity.exportGoalResults,
      {
        goalId: payload,
      },
    )
    //const arrayBuffer = base64ToArrayBuffer(String(fileContent))
    download(fileName, String(fileContent), fileMediaType)
  } catch (e: any) {
    console.log('GetCurrentGoalExport failed: ', e)

    let msg: JSX.Element | string = getErrorMsg('export')

    if (e.status === 401) {
      msg = t`Unauthorized!`
    }

    const getUserActivityErrorNotification: INotification = {
      type: 'warning',
      content: msg,
    }

    yield put({ type: String(addNotification), payload: getUserActivityErrorNotification })
  }
}

export interface IJoinGoalPayload {
  goalId: GoalId
  progressType: ProgressType
  customType?: CustomType | null
  manualEntryEnabled?: boolean

  // For refreshChallengeResults.
  isPoints?: boolean
  pointsLogic?: string
  startDateFormatted?: string
  endDateFormatted?: string
}

function* sagaJoinGoal({ payload }: Action<IJoinGoalPayload>) {
  try {
    yield put({ type: String(setLoadingJoinGoal) })
    yield put({ type: String(clearNotification) })
    const { goalId, progressType, customType, manualEntryEnabled } = payload
    const { serviceProvider: serviceProviderRaw, dateOfBirth }: UserProfileDto = yield select(getUserDto)
    const userIntegrationScopes: string = yield select(getUserIntegrationScopes)

    const isStepChallenge = isStepsType(progressType)
    const isActivityIndex = isActivityIndexType(progressType)
    //const isDistance = isDistanceType(customType)

    /*if (
      isDistance &&
      serviceProviderRaw === ServiceProvider.GOOGLE_FIT &&
      !userIntegrationScopes.includes('fitness.location.read')
    ) {
      const serviceProvider = getServiceProviderLabel(serviceProviderRaw)
      const msg = t`We seem to have insufficient permissions to sync with your ${serviceProvider} integration.`
      const notification: INotification = {
        type: 'warning',
        //content: msg,
        content: (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              width: '100%',
            }}
          >
            <span
              style={{
                margin: '10px 0',
                flexGrow: 1,
              }}
            >
              {msg}
            </span>{' '}
            <YumButton size="small" to={ROUTES.SETTINGS.to}>{t`Choose integration`}</YumButton>
          </div>
        ),
      }
      yield put({ type: String(addNotification), payload: notification })
      scrollToTop()
      return
    }*/

    const leanScope = !dateOfBirth
    const noIntegration = serviceProviderRaw === null

    const isManualEntryOnly = isManualEntryOnlyType(progressType, customType)
    const supportsManualEntry = manualEntryEnabled || isManualEntryOnly
    // console.log('supportsManualEntry', supportsManualEntry)
    // console.log('isManualEntryOnly', isManualEntryOnly)

    if (isActivityIndex && leanScope && (!noIntegration || manualEntryEnabled)) {
      const msg =
        currentLang === Lang.EN || currentLang === Lang.ET
          ? t`If you want to take part in this challenge, please provide additional data.`
          : t`If you want to take part in this challenge, please provide additional data and connect a heart rate activity device to the platform.`
      const notification: INotification = {
        type: 'warning',
        //content: msg,
        content: (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              width: '100%',
            }}
          >
            <span
              style={{
                margin: '10px 0',
                flexGrow: 1,
              }}
            >
              {msg}
            </span>{' '}
            <YumSetupActivityDataWrapper />
          </div>
        ),
      }
      yield put({ type: String(addNotification), payload: notification })
      scrollToTop()
      return
    }
    // No integration + manual entry disabled.
    else if (noIntegration && !supportsManualEntry) {
      const msg = t`Before you can join this challenge, you need to have a working integration.`
      const notification: INotification = {
        type: 'warning',
        //content: msg,
        content: (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              width: '100%',
            }}
          >
            <span
              style={{
                margin: '10px 0',
                flexGrow: 1,
              }}
            >
              {msg}
            </span>{' '}
            <YumChooseIntegrationBtnWrapper buttonText={t`Choose integration`} />
          </div>
        ),
      }
      yield put({ type: String(addNotification), payload: notification })
      scrollToTop()
      return
    }

    yield call(api.activity.joinGoal, { goalId })
    const { goals }: GetGoalsWithProgress$Response = yield call(api.activity.getChallengesWithProgress, { version })
    yield put({ type: String(getCurrentCompanyGoalSuccess), payload: goals ? goals : [] })

    const { isPoints, pointsLogic, startDateFormatted, endDateFormatted } = payload
    const isAnyActivityIndex = [ProgressType.ALL, ProgressType.PERSISTENCE_ALL].includes(progressType)
    const isCollective = isCollectiveType(progressType)
    yield put({
      type: String(refreshChallengeResults),
      payload: {
        goalId,
        customType: getCustomTypeWithLegacy(customType || null, progressType),
        startDate: startDateFormatted,
        endDate: endDateFormatted,
        participating: true,
        isCollective,
        preventListUpdate: false,
        isActivityIndex: isAnyActivityIndex,
        manualEntryEnabled,
        pointsLogic,
        isPoints,
      },
    })

    // yield put({ type: String(joinGoalSuccess), payload: { goalId } })
    yield put({ type: String(setLoadingJoinGoalDone) })
  } catch (e: any) {
    console.error('JoinGoal failed: ', e)
    // yield put({ type: String(joinGoalFail), payload: { goalId: payload.goalId } })
    yield put({ type: String(setLoadingJoinGoalDone) })
  }
}

function* sagaLeaveGoal({ payload }: Action<LeaveGoal$Request>) {
  try {
    yield put({ type: String(clearNotification) })
    yield call(api.activity.leaveGoal, payload)

    const { goals }: GetGoalsWithProgress$Response = yield call(api.activity.getChallengesWithProgress, { version })
    yield put({ type: String(getCurrentCompanyGoalSuccess), payload: goals ? goals : [] })
  } catch (e: any) {
    console.error('LeaveGoal failed: ', e)
  }
}

export function* activityManagementSagas() {
  yield all([
    takeEvery(String(getCurrentGoalActivityList), sagaGetCurrentGoalActivityList),
    takeEvery(String(getCurrentLeaderboard), sagaGetCurrentLeaderboard),
    takeEvery(String(getCurrentCompanyGoal), sagaGetCurrentCompanyGoal),
    takeEvery(String(getCurrentGoalExport), sagaGetCurrentGoalExport),
    takeEvery(String(joinGoal), sagaJoinGoal),
    takeEvery(String(leaveGoal), sagaLeaveGoal),
  ])
}
